import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useBoolean, useDebounce, usePrevious } from 'react-use'

import { DEFAULT_DEBOUNCE_MS } from './constants'

type ItemOrder = 'asc' | 'desc'

/**
 * this hook handle
 * - update list of items from effect
 * - set page state for pagination including search param for `?page=`
 * - set searching state including search param for `?search=`
 * - set sorting or ordering type `?sort=`
 * - set sorting by field `?sortBy=`
 */
export function useQueryParam<SortBy>(debounceMs = DEFAULT_DEBOUNCE_MS) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isLoading, setIsLoading] = useBoolean(false)

  const [page, setPage] = useState(Number(searchParams.get('page')) || 1)
  const [sort, setSort] = useState<ItemOrder | undefined>((searchParams.get('sort') as ItemOrder) || undefined)

  const paramSortBy = searchParams.get('sortBy') as unknown as SortBy
  const [sortBy, setSortBy] = useState(paramSortBy || undefined)

  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [debounceSearch, setDebounceSearch] = useState(searchParams.get('search') || '')
  const prevSearch = usePrevious(debounceSearch)

  const [, cancelDebounceSearch] = useDebounce(
    () => {
      setDebounceSearch(search)
    },
    debounceMs,
    [search]
  )

  useEffect(() => {
    /**
     * only change searchParams if this equal true
     */
    let hasChange = false

    if (page) {
      const curPage = searchParams.get('page')
      /**
       * do not add ?page=1 on initial url `/some-url`
       */
      const notFirstPage = !curPage && page > 1
      /**
       * has ?page and value is different
       */
      const differentPage = curPage && page !== Number(curPage)

      if (notFirstPage || differentPage) {
        hasChange = true
        searchParams.set('page', page.toString())
      }
    }

    /**
     * handle sort param
     */
    const oldSort = searchParams.get('sort')
    if (sort) {
      if (sort !== oldSort) {
        hasChange = true
        searchParams.set('sort', sort)
      }
    } else {
      if (oldSort) {
        hasChange = true
        searchParams.delete('sort')
      }
    }

    /**
     * handle sort by param
     */
    const oldSortBy = searchParams.get('sortBy')
    if (sortBy) {
      const newSortBy = (sortBy as unknown as string).toString()
      if (newSortBy !== oldSortBy) {
        hasChange = true
        searchParams.set('sortBy', newSortBy)
      }
    } else {
      if (oldSortBy) {
        hasChange = true
        searchParams.delete('sortBy')
      }
    }

    /**
     * handle search param
     */
    const oldSearch = searchParams.get('search')
    if (debounceSearch) {
      if (debounceSearch !== oldSearch) {
        hasChange = true
        searchParams.set('search', debounceSearch)
      }
    } else {
      if (oldSearch) {
        hasChange = true
        searchParams.delete('search')
      }
    }

    /**
     * set search param if there is a change
     * and update url search params
     */
    if (hasChange) {
      setSearchParams(searchParams)
    }
  }, [debounceSearch, page, sort, sortBy, searchParams, setSearchParams])

  /**
   * update search params when previous search state
   * and current search state differs
   */
  useEffect(() => {
    if (prevSearch !== undefined && prevSearch !== debounceSearch && page !== 1) {
      setPage(1)
    }
  }, [setPage, page, prevSearch, debounceSearch])

  const resetParams = () => {
    cancelDebounceSearch()

    if (searchParams.get('search')) {
      searchParams.delete('search')
    }
    if (searchParams.get('page')) {
      searchParams.set('page', '1')
    }
    setSearchParams(searchParams)
    setPage(1)
    setSearch('')
    setDebounceSearch('')
  }

  return {
    isLoading,
    setIsLoading,
    searchParams,
    setSearchParams,
    debounceSearch,
    setDebounceSearch,
    cancelDebounceSearch,
    search,
    setSearch,
    prevSearch,
    page,
    setPage,
    sort,
    setSort,
    sortBy,
    setSortBy,
    resetParams,
  }
}
